import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import { Trash2 } from "lucide-react";

import { UIModal, UIUpload } from "../../../ui";
import ClientReportFileRow from "./ClientReportFileRow";
import {
  CLIENT_REPORT_ACCEPT,
  CLIENT_REPORT_MAX_SIZE_BYTES,
  CLIENT_REPORT_MAX_SIZE_LABEL,
  CLIENT_REPORT_MIME_TYPES,
} from "./constants";
import {
  formatClientReportSize,
  resolveClientReportFileType,
} from "./clientReportMappers";
import { uploadClientReportFile } from "./clientReportUpload";

import "./ClientReports.scss";

const UploadClientReportModal = ({
  open,
  labels,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [file, setFile] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploadPercent(null);
    }
  }, [open]);

  const isUploading = uploadPercent !== null;
  const busy = submitting || isUploading;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!file || busy) return;

    setUploadPercent(0);
    try {
      const uploadUrl = await uploadClientReportFile(file, {
        onProgress: setUploadPercent,
      });
      setUploadPercent(100);
      await onSubmit(file, uploadUrl);
    } catch (error) {
      // the upload helper surfaces its own error message
    } finally {
      // always clear, otherwise a failed registration leaves the modal busy-locked
      setUploadPercent(null);
    }
  };

  return (
    <UIModal
      open={open}
      type="info"
      title={labels.title}
      description={labels.description}
      cancelLabel={labels.cancel}
      confirmLabel={labels.submit}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      cancelButtonProps={{ disabled: busy }}
      confirmButtonProps={{
        disabled: !file || busy,
        appearance: "gradient",
        loading: submitting,
      }}
      PaperProps={{ className: "client-report-modal" }}
      maxWidth="sm"
      fullWidth
    >
      <div className="client-report-modal__body">
        {/* `file`/`preview` stay empty so UIUpload keeps showing the dropzone —
            the picked file is rendered below as the design's file row. */}
        <UIUpload
          id="upload-client-report"
          name="clientReportFile"
          accept={CLIENT_REPORT_ACCEPT}
          acceptedTypes={CLIENT_REPORT_MIME_TYPES}
          maxSizeBytes={CLIENT_REPORT_MAX_SIZE_BYTES}
          maxSizeLabel={CLIENT_REPORT_MAX_SIZE_LABEL}
          invalidTypeMessage={labels.uploadTypeError}
          invalidSizeMessage={labels.uploadMaxSizeError}
          uploadText={labels.uploadClickToUpload}
          dragHint={labels.uploadDragHint}
          hint={labels.uploadHint}
          disabled={busy}
          onChange={(nextFile) => setFile(nextFile)}
        />

        {file ? (
          <ClientReportFileRow
            title={file.name}
            subtitle={[labels.readyToUpload, formatClientReportSize(file.size)]
              .filter(Boolean)
              .join(" • ")}
            fileType={resolveClientReportFileType(file.type, file.name)}
            progress={isUploading ? uploadPercent : undefined}
            progressLabel={isUploading ? `${uploadPercent}%` : null}
            action={
              <IconButton
                type="button"
                className="client-report-file-row__action"
                aria-label={labels.removeFile}
                disabled={busy}
                onClick={() => setFile(null)}
              >
                <Trash2 size={18} strokeWidth={1.75} />
              </IconButton>
            }
          />
        ) : null}
      </div>
    </UIModal>
  );
};

UploadClientReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    title: PropTypes.node.isRequired,
    description: PropTypes.node,
    cancel: PropTypes.node.isRequired,
    submit: PropTypes.node.isRequired,
    uploadClickToUpload: PropTypes.node,
    uploadDragHint: PropTypes.node,
    uploadHint: PropTypes.node,
    uploadTypeError: PropTypes.string,
    uploadMaxSizeError: PropTypes.string,
    readyToUpload: PropTypes.string,
    removeFile: PropTypes.string,
  }).isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

UploadClientReportModal.defaultProps = {
  submitting: false,
};

export default UploadClientReportModal;
